/**
 * The guide editor's document model (Guide roadmap G5): pure TypeScript, no React.
 *
 * The editor never sends a replacement document. It keeps the guide as the server
 * last returned it (`base`) and a queue of typed operations, in the exact shapes
 * the Content API's `POST /guides/{slug}/edits` accepts (Curated-By-Blog
 * docs/guides/GUIDE_SYSTEM_REFERENCE.md section 4.2.1). What the forms show is
 * `deriveDraft(base, queue)`: the base with the queued operations applied locally,
 * so the screen reflects an edit before it is saved. Applying locally is only a
 * preview; the server re-applies the same operations to its own copy, re-derives
 * pins, badges and counts, and is the authority.
 *
 * Operations that need the server to decide something (`add_pick_from_place`,
 * `set_venue_link`, `add_chapter`: a place is resolved, an id is derived) are never
 * left in the queue. They are sent at once, together with whatever is queued, and
 * the editor reloads the guide.
 */

// ─── the document (only what the editor reads) ───────────────────────────────

export type TitleNode = { text?: string; accent?: string } | string | null | undefined;

export interface EditorVenue {
  name?: TitleNode;
  address?: string;
  lat?: number;
  lng?: number;
  placeId?: string;
  locationId?: string;
  pinNumber?: number;
  businessStatus?: string;
  websiteUrl?: string;
  nativeName?: string;
  [key: string]: unknown;
}

export interface EditorPick {
  id: string;
  type?: string;
  title?: TitleNode;
  kicker?: string;
  meta?: string[];
  paragraphs?: string[];
  caption?: string;
  marginalia?: string;
  imageUrl?: string;
  polaroid?: { imageUrl?: string; caption?: string } | null;
  locationId?: string;
  badge?: { number?: number } | null;
  [key: string]: unknown;
}

export interface EditorChapter {
  id: string;
  kicker?: string;
  dek?: string;
  color?: string;
  title?: TitleNode;
  picks?: EditorPick[];
  map?: { venues?: EditorVenue[] } | null;
  [key: string]: unknown;
}

export interface EditorGuide {
  slug?: string;
  title?: TitleNode;
  dek?: string;
  city?: string;
  category?: string;
  draft?: boolean;
  revision?: string;
  lede?: { headline?: TitleNode; paragraphs?: string[] } | null;
  seo?: { title?: string; description?: string } | null;
  chapters?: EditorChapter[];
  [key: string]: unknown;
}

// ─── the operations (a closed set, as the Content API defines them) ──────────

/** A paragraph op acts on a pick's paragraphs or on the lede's: exactly one of the two. */
export type ParagraphTarget =
  | { pickId: string; target?: undefined }
  | { target: 'lede'; pickId?: undefined };

export type GuideOp =
  | { op: 'set_text'; path: string; value: string }
  | ({ op: 'insert_paragraph'; index: number; value?: string } & ParagraphTarget)
  | ({ op: 'remove_paragraph'; index: number } & ParagraphTarget)
  | ({ op: 'move_paragraph'; from: number; to: number } & ParagraphTarget)
  | { op: 'remove_meta'; pickId: string; index: number }
  | { op: 'remove_pick'; pickId: string }
  | { op: 'move_pick'; pickId: string; chapterId: string; index: number }
  | {
      op: 'add_pick_from_place';
      chapterId: string;
      index: number;
      locationId?: string;
      placeId?: string;
      title?: string;
      paragraphs?: string[];
    }
  | { op: 'set_venue_link'; pickId: string; locationId?: string; placeId?: string }
  | {
      op: 'add_chapter';
      title: string;
      index?: number;
      kicker?: string;
      dek?: string;
      color?: 'gold' | 'mint' | 'peach' | 'gray' | 'blue';
    }
  | { op: 'rename_chapter'; chapterId: string; title: string; kicker?: string }
  | { op: 'move_chapter'; chapterId: string; index: number }
  | { op: 'remove_chapter'; chapterId: string };

export interface QueuedOp {
  /** Local id: stable across re-ordering, used to attach server errors. */
  id: number;
  op: GuideOp;
}

/** Operations the server must resolve; they are sent at once, never queued. */
export const SERVER_RESOLVED_OPS = ['add_pick_from_place', 'set_venue_link', 'add_chapter'] as const;

// Limits from the Content API's `set_text` table (and its per-pick / per-chapter caps).
export const MAX_PARAGRAPHS = 8;
export const MAX_META = 6;
export const MIN_LEDE_PARAGRAPHS = 1;
export const MAX_OPS = 100;

export const LIMITS = {
  'title.text': 200,
  'title.accent': 200,
  dek: 400,
  'lede.headline': 200,
  'lede.paragraph': 4000,
  'chapter.title': 160,
  'chapter.kicker': 120,
  'chapter.dek': 400,
  'pick.title': 200,
  'pick.kicker': 120,
  'pick.meta': 160,
  'pick.paragraph': 4000,
  'pick.caption': 240,
  'pick.marginalia': 600,
  'seo.title': 120,
  'seo.description': 200,
} as const;

// ─── small helpers ───────────────────────────────────────────────────────────

export function titleText(node: TitleNode): string {
  if (typeof node === 'string') return node;
  return node && typeof node.text === 'string' ? node.text : '';
}

export function titleAccent(node: TitleNode): string {
  return node && typeof node === 'object' && typeof node.accent === 'string' ? node.accent : '';
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export const chapterPath = (id: string, field: 'title' | 'dek') => `chapters[${id}].${field}`;
export const pickPath = (
  id: string,
  field: 'title' | 'kicker' | 'caption' | 'marginalia'
) => `picks[${id}].${field}`;
export const pickParagraphPath = (id: string, i: number) => `picks[${id}].paragraphs[${i}]`;
export const pickMetaPath = (id: string, i: number) => `picks[${id}].meta[${i}]`;
export const ledeParagraphPath = (i: number) => `lede.paragraphs[${i}]`;

/** An id the Content API cannot address from a path (it splits on brackets). */
export function isPathSafeId(id: unknown): id is string {
  return typeof id === 'string' && id.length > 0 && !/[[\]]/.test(id);
}

export function findChapter(
  guide: EditorGuide,
  chapterId: string
): { chapter: EditorChapter; index: number } | null {
  const chapters = guide.chapters ?? [];
  const index = chapters.findIndex((c) => c && c.id === chapterId);
  return index < 0 ? null : { chapter: chapters[index], index };
}

export function findPick(
  guide: EditorGuide,
  pickId: string
): { pick: EditorPick; chapter: EditorChapter; index: number } | null {
  for (const chapter of guide.chapters ?? []) {
    const picks = chapter.picks ?? [];
    const index = picks.findIndex((p) => p && p.id === pickId);
    if (index >= 0) return { pick: picks[index], chapter, index };
  }
  return null;
}

export function allPicks(guide: EditorGuide): EditorPick[] {
  return (guide.chapters ?? []).flatMap((c) => c.picks ?? []);
}

/**
 * Pick ids the Content API refuses to address: used by more than one pick ("ids are
 * meant to be unique within a chapter; the composer does not guarantee more"), or
 * carrying a bracket. The editor shows those picks read-only rather than send an op
 * that would be refused.
 */
export function unaddressablePickIds(guide: EditorGuide): Set<string> {
  const seen = new Map<string, number>();
  for (const pick of allPicks(guide)) seen.set(pick.id, (seen.get(pick.id) ?? 0) + 1);
  const bad = new Set<string>();
  seen.forEach((count, id) => {
    if (count > 1 || !isPathSafeId(id)) bad.add(id);
  });
  return bad;
}

/** A pick with no words: the Content API's `empty_pick_copy` rule (a `compare` pick is exempt). */
export function pickNeedsCopy(pick: EditorPick): boolean {
  if (pick.type === 'compare') return false;
  return !(pick.paragraphs ?? []).some((p) => typeof p === 'string' && p.trim().length > 0);
}

const slug = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
const norm = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, '');

/** The chapter-map venue a pick describes (a twin of the server's `match_pick_venue`). */
export function matchPickVenue(pick: EditorPick, chapter: EditorChapter): EditorVenue | null {
  const venues = (chapter.map?.venues ?? []).filter((v) => v && typeof v === 'object');
  if (venues.length === 0) return null;
  const badge = pick.badge;
  if (badge && typeof badge === 'object') {
    const byBadge = venues.find((v) => v.pinNumber === badge.number);
    if (byBadge) return byBadge;
  }
  if (pick.id) {
    const byId = venues.find((v) => slug(titleText(v.name)).slice(0, 48) === pick.id);
    if (byId) return byId;
  }
  const title = norm(titleText(pick.title));
  if (title) {
    const byTitle = venues.find((v) => norm(titleText(v.name)) === title);
    if (byTitle) return byTitle;
  }
  return null;
}

export function pickImage(pick: EditorPick): string | null {
  const src = pick.imageUrl || pick.polaroid?.imageUrl;
  return typeof src === 'string' && src.trim() ? src.trim() : null;
}

/** A pick the page shows an image for: only then does `picks[p].caption` exist. */
export function pickHasCaption(pick: EditorPick): boolean {
  return pick.type === 'split' || pick.type === 'feature' || !!pick.polaroid;
}

export function pickCaption(pick: EditorPick): string {
  const holder = pick.type === 'split' || pick.type === 'feature' ? pick : pick.polaroid;
  return holder && typeof holder.caption === 'string' ? holder.caption : '';
}

// ─── reading and writing a `set_text` path on the local draft ────────────────

type Parsed =
  | { kind: 'title.text' | 'title.accent' | 'dek' | 'lede.headline' | 'seo.title' | 'seo.description' }
  | { kind: 'lede.paragraph'; index: number }
  | { kind: 'chapter'; id: string; field: 'title' | 'dek' }
  | {
      kind: 'pick';
      id: string;
      field: 'title' | 'kicker' | 'caption' | 'marginalia' | 'meta' | 'paragraph';
      index?: number;
    };

const TOP_PATHS = ['title.text', 'title.accent', 'dek', 'lede.headline', 'seo.title', 'seo.description'];
const RE_LEDE = /^lede\.paragraphs\[(\d+)\]$/;
const RE_CHAPTER = /^chapters\[([^[\]]+)\]\.(title|dek)$/;
const RE_PICK = /^picks\[([^[\]]+)\]\.(title|kicker|caption|marginalia|meta\[(\d+)\]|paragraphs\[(\d+)\])$/;

export function parsePath(path: string): Parsed | null {
  if (TOP_PATHS.includes(path)) return { kind: path as never };
  let m = RE_LEDE.exec(path);
  if (m) return { kind: 'lede.paragraph', index: Number(m[1]) };
  m = RE_CHAPTER.exec(path);
  if (m) return { kind: 'chapter', id: m[1], field: m[2] as 'title' | 'dek' };
  m = RE_PICK.exec(path);
  if (m) {
    if (m[3] !== undefined) return { kind: 'pick', id: m[1], field: 'meta', index: Number(m[3]) };
    if (m[4] !== undefined) return { kind: 'pick', id: m[1], field: 'paragraph', index: Number(m[4]) };
    return { kind: 'pick', id: m[1], field: m[2] as 'title' | 'kicker' | 'caption' | 'marginalia' };
  }
  return null;
}

/** The character limit for a path, or null for a path the editor does not know. */
export function limitForPath(path: string): number | null {
  const p = parsePath(path);
  if (!p) return null;
  switch (p.kind) {
    case 'title.text':
    case 'title.accent':
    case 'dek':
    case 'seo.title':
    case 'seo.description':
    case 'lede.headline':
      return LIMITS[p.kind];
    case 'lede.paragraph':
      return LIMITS['lede.paragraph'];
    case 'chapter':
      return LIMITS[p.field === 'title' ? 'chapter.title' : 'chapter.dek'];
    case 'pick':
      return {
        title: LIMITS['pick.title'],
        kicker: LIMITS['pick.kicker'],
        caption: LIMITS['pick.caption'],
        marginalia: LIMITS['pick.marginalia'],
        meta: LIMITS['pick.meta'],
        paragraph: LIMITS['pick.paragraph'],
      }[p.field];
  }
}

/** The Content API requires these to be non-blank. */
export function isRequiredPath(path: string): boolean {
  const p = parsePath(path);
  if (!p) return false;
  if (p.kind === 'title.text' || p.kind === 'lede.headline') return true;
  if (p.kind === 'chapter') return p.field === 'title';
  if (p.kind === 'pick') return p.field === 'title' || p.field === 'meta';
  return false;
}

function titleNodeOf(owner: Record<string, unknown>, key: string): { text?: string; accent?: string } {
  const node = owner[key];
  if (node && typeof node === 'object') return node as { text?: string; accent?: string };
  const made = { text: typeof node === 'string' ? node : '' };
  owner[key] = made;
  return made;
}

function setTitle(owner: Record<string, unknown>, key: string, text: string): void {
  const node = titleNodeOf(owner, key);
  node.text = text;
  // The server drops an accent that is no longer in the text; mirror it.
  if (node.accent && !text.includes(node.accent)) delete node.accent;
}

/** The value a path holds in a guide, as the editor would show it ('' when absent). */
export function readPath(guide: EditorGuide, path: string): string {
  const p = parsePath(path);
  if (!p) return '';
  switch (p.kind) {
    case 'title.text':
      return titleText(guide.title);
    case 'title.accent':
      return titleAccent(guide.title);
    case 'dek':
      return guide.dek ?? '';
    case 'lede.headline':
      return titleText(guide.lede?.headline);
    case 'seo.title':
      return guide.seo?.title ?? '';
    case 'seo.description':
      return guide.seo?.description ?? '';
    case 'lede.paragraph':
      return guide.lede?.paragraphs?.[p.index] ?? '';
    case 'chapter': {
      const found = findChapter(guide, p.id);
      if (!found) return '';
      return p.field === 'title' ? titleText(found.chapter.title) : found.chapter.dek ?? '';
    }
    case 'pick': {
      const found = findPick(guide, p.id);
      if (!found) return '';
      const { pick } = found;
      if (p.field === 'title') return titleText(pick.title);
      if (p.field === 'kicker') return pick.kicker ?? '';
      if (p.field === 'caption') return pickCaption(pick);
      if (p.field === 'marginalia') return pick.marginalia ?? '';
      if (p.field === 'meta') return pick.meta?.[p.index ?? 0] ?? '';
      return pick.paragraphs?.[p.index ?? 0] ?? '';
    }
  }
}

function writePath(guide: EditorGuide, path: string, value: string): void {
  const p = parsePath(path);
  if (!p) return;
  const g = guide as Record<string, unknown>;
  switch (p.kind) {
    case 'title.text':
      return setTitle(g, 'title', value);
    case 'title.accent': {
      const node = titleNodeOf(g, 'title');
      if (value) node.accent = value;
      else delete node.accent;
      return;
    }
    case 'dek':
      guide.dek = value;
      return;
    case 'lede.headline':
      if (guide.lede) setTitle(guide.lede as Record<string, unknown>, 'headline', value);
      return;
    case 'seo.title':
    case 'seo.description': {
      const key = p.kind === 'seo.title' ? 'title' : 'description';
      const seo = (guide.seo = guide.seo ?? {});
      if (value) seo[key] = value;
      else delete seo[key];
      return;
    }
    case 'lede.paragraph': {
      const paragraphs = guide.lede?.paragraphs;
      if (paragraphs && p.index < paragraphs.length) paragraphs[p.index] = value;
      return;
    }
    case 'chapter': {
      const found = findChapter(guide, p.id);
      if (!found) return;
      if (p.field === 'title') setTitle(found.chapter as Record<string, unknown>, 'title', value);
      else if (value) found.chapter.dek = value;
      else delete found.chapter.dek;
      return;
    }
    case 'pick': {
      const found = findPick(guide, p.id);
      if (!found) return;
      const { pick } = found;
      if (p.field === 'title') setTitle(pick as Record<string, unknown>, 'title', value);
      else if (p.field === 'kicker') {
        if (value) pick.kicker = value;
        else delete pick.kicker;
      } else if (p.field === 'caption') {
        const holder = pick.type === 'split' || pick.type === 'feature' ? pick : pick.polaroid;
        if (holder) holder.caption = value;
      } else if (p.field === 'marginalia') pick.marginalia = value;
      else if (p.field === 'meta') {
        const meta = (pick.meta = Array.isArray(pick.meta) ? pick.meta : []);
        const i = p.index ?? 0;
        if (i === meta.length) meta.push(value);
        else if (i < meta.length) meta[i] = value;
      } else {
        const paragraphs = pick.paragraphs;
        if (paragraphs && (p.index ?? 0) < paragraphs.length) paragraphs[p.index ?? 0] = value;
      }
      return;
    }
  }
}

// ─── applying an operation to the local draft ────────────────────────────────

/** The paragraph list a paragraph op acts on (the lede's or a pick's); `create` makes a missing one. */
function paragraphsOf(guide: EditorGuide, op: ParagraphTarget, create = false): string[] | undefined {
  if (op.target === 'lede') {
    if (!guide.lede) return undefined;
    if (create && !guide.lede.paragraphs) guide.lede.paragraphs = [];
    return guide.lede.paragraphs;
  }
  const found = findPick(guide, op.pickId as string);
  if (!found) return undefined;
  if (create && !found.pick.paragraphs) found.pick.paragraphs = [];
  return found.pick.paragraphs;
}

function applyOp(guide: EditorGuide, op: GuideOp): void {
  switch (op.op) {
    case 'set_text':
      writePath(guide, op.path, op.value);
      return;
    case 'insert_paragraph': {
      const paragraphs = paragraphsOf(guide, op, true);
      paragraphs?.splice(Math.min(op.index, paragraphs.length), 0, op.value ?? '');
      return;
    }
    case 'remove_paragraph': {
      const paragraphs = paragraphsOf(guide, op);
      if (paragraphs && op.index < paragraphs.length) paragraphs.splice(op.index, 1);
      return;
    }
    case 'move_paragraph': {
      const paragraphs = paragraphsOf(guide, op);
      if (!paragraphs || op.from >= paragraphs.length) return;
      const [moved] = paragraphs.splice(op.from, 1);
      paragraphs.splice(Math.min(op.to, paragraphs.length), 0, moved);
      return;
    }
    case 'remove_meta': {
      const found = findPick(guide, op.pickId);
      const meta = found?.pick.meta;
      if (!found || !meta || op.index >= meta.length) return;
      meta.splice(op.index, 1);
      // Removing the last line drops the key, as the API does.
      if (meta.length === 0) delete found.pick.meta;
      return;
    }
    case 'remove_pick': {
      const found = findPick(guide, op.pickId);
      if (found) found.chapter.picks?.splice(found.index, 1);
      return;
    }
    case 'move_pick': {
      const found = findPick(guide, op.pickId);
      const target = findChapter(guide, op.chapterId);
      if (!found || !target || !found.chapter.picks) return;
      const [pick] = found.chapter.picks.splice(found.index, 1);
      const picks = (target.chapter.picks = target.chapter.picks ?? []);
      picks.splice(Math.min(op.index, picks.length), 0, pick);
      return;
    }
    case 'rename_chapter': {
      const found = findChapter(guide, op.chapterId);
      if (!found) return;
      setTitle(found.chapter as Record<string, unknown>, 'title', op.title);
      if (op.kicker !== undefined) found.chapter.kicker = op.kicker;
      return;
    }
    case 'move_chapter': {
      const chapters = guide.chapters ?? [];
      const found = findChapter(guide, op.chapterId);
      if (!found) return;
      const [chapter] = chapters.splice(found.index, 1);
      chapters.splice(Math.min(op.index, chapters.length), 0, chapter);
      return;
    }
    case 'remove_chapter': {
      const found = findChapter(guide, op.chapterId);
      if (found && !(found.chapter.picks ?? []).length) guide.chapters?.splice(found.index, 1);
      return;
    }
    default:
      // add_pick_from_place, set_venue_link, add_chapter: the server decides (never queued).
      return;
  }
}

/** The base guide with the queued operations applied, for display. Never mutates `base`. */
export function deriveDraft(base: EditorGuide, queue: QueuedOp[]): EditorGuide {
  const draft = clone(base);
  for (const { op } of queue) {
    try {
      applyOp(draft, op);
    } catch {
      // A local preview that cannot apply an op (the guide moved under it) is left
      // as it was; the server reports the op properly when the editor saves.
    }
  }
  return draft;
}

// ─── the queue ───────────────────────────────────────────────────────────────

/** Where the trailing run of text-like edits (set_text, rename_chapter) starts. */
function trailingTextRun(queue: QueuedOp[]): number {
  let cut = queue.length;
  while (cut > 0 && (queue[cut - 1].op.op === 'set_text' || queue[cut - 1].op.op === 'rename_chapter')) cut--;
  return cut;
}

/**
 * Queue a text edit. Repeated edits of one field collapse into one operation (only
 * within the trailing run of text edits, so a structural operation between two edits
 * of the same field is never reordered around), and an edit that puts a field back to
 * what the guide already holds removes the operation, so the unsaved count goes down.
 */
export function enqueueSetText(
  base: EditorGuide,
  queue: QueuedOp[],
  path: string,
  value: string,
  id: number
): QueuedOp[] {
  const cut = trailingTextRun(queue);
  const rest = [
    ...queue.slice(0, cut),
    ...queue.slice(cut).filter((q) => !(q.op.op === 'set_text' && q.op.path === path)),
  ];
  if (readPath(deriveDraft(base, rest), path) === value) return rest;
  return [...rest, { id, op: { op: 'set_text', path, value } }];
}

/** Queue a chapter rename: a later rename of the same chapter in the trailing text run replaces it. */
export function enqueueRenameChapter(
  queue: QueuedOp[],
  op: Extract<GuideOp, { op: 'rename_chapter' }>,
  id: number
): QueuedOp[] {
  const cut = trailingTextRun(queue);
  const rest = [
    ...queue.slice(0, cut),
    ...queue
      .slice(cut)
      .filter((q) => !(q.op.op === 'rename_chapter' && q.op.chapterId === op.chapterId)),
  ];
  return [...rest, { id, op }];
}

/**
 * Drop the queued operations that point at a pick that is about to be removed, then
 * queue the removal. (Removing a pick makes its pending text edits meaningless, and an
 * edit to a pick the guide no longer has would be reported as a failure.)
 */
export function enqueueRemovePick(queue: QueuedOp[], pickId: string, id: number): QueuedOp[] {
  const kept = queue.filter((q) => !opsTouchingPick(q.op, pickId));
  return [...kept, { id, op: { op: 'remove_pick', pickId } }];
}

function opsTouchingPick(op: GuideOp, pickId: string): boolean {
  if (op.op === 'set_text') {
    const p = parsePath(op.path);
    return !!p && p.kind === 'pick' && p.id === pickId;
  }
  return (
    (op.op === 'insert_paragraph' ||
      op.op === 'remove_paragraph' ||
      op.op === 'move_paragraph' ||
      op.op === 'remove_meta' ||
      op.op === 'move_pick') &&
    op.pickId === pickId
  );
}

// ─── validation, dirty state and error attachment ────────────────────────────

/** The field keys an operation acts on, matched against what each form field asks for. */
export function opTargets(op: GuideOp): string[] {
  switch (op.op) {
    case 'set_text':
      return [op.path];
    case 'insert_paragraph':
    case 'remove_paragraph':
    case 'move_paragraph':
      return op.target === 'lede'
        ? ['lede.paragraphs', 'lede']
        : [`picks[${op.pickId}].paragraphs`, `picks[${op.pickId}]`];
    case 'remove_meta':
      return [`picks[${op.pickId}].meta`, `picks[${op.pickId}]`];
    case 'remove_pick':
    case 'move_pick':
    case 'set_venue_link':
      return [`picks[${op.pickId}]`];
    case 'rename_chapter':
      return [`chapters[${op.chapterId}].title`, `chapters[${op.chapterId}].kicker`, `chapters[${op.chapterId}]`];
    case 'move_chapter':
    case 'remove_chapter':
      return [`chapters[${op.chapterId}]`];
    case 'add_pick_from_place':
      return [`chapters[${op.chapterId}]`];
    default:
      return [];
  }
}

/** The outline item an operation belongs to, so an error can link to it. */
export function opItem(op: GuideOp, guide: EditorGuide): Selection {
  switch (op.op) {
    case 'set_text': {
      const p = parsePath(op.path);
      if (p?.kind === 'pick') return { kind: 'pick', id: p.id };
      if (p?.kind === 'chapter') return { kind: 'chapter', id: p.id };
      return { kind: 'guide' };
    }
    case 'insert_paragraph':
    case 'remove_paragraph':
    case 'move_paragraph':
      if (op.target === 'lede') return { kind: 'guide' };
      return findPick(guide, op.pickId as string) ? { kind: 'pick', id: op.pickId as string } : { kind: 'guide' };
    case 'remove_meta':
    case 'remove_pick':
    case 'move_pick':
    case 'set_venue_link':
      return findPick(guide, op.pickId) ? { kind: 'pick', id: op.pickId } : { kind: 'guide' };
    case 'rename_chapter':
    case 'move_chapter':
    case 'remove_chapter':
    case 'add_pick_from_place':
      return findChapter(guide, op.chapterId) ? { kind: 'chapter', id: op.chapterId } : { kind: 'guide' };
    default:
      return { kind: 'guide' };
  }
}

export type Selection =
  | { kind: 'guide' }
  | { kind: 'chapter'; id: string }
  | { kind: 'pick'; id: string };

export const selectionKey = (s: Selection) => (s.kind === 'guide' ? 'guide' : `${s.kind}:${s.id}`);

/**
 * Problems the editor can see before it asks the server, keyed by queued op id: a
 * value over its limit, a required field left blank, an accent that is not in its
 * title. The server checks all of these again; this only stops a doomed save.
 */
export function validateQueue(draft: EditorGuide, queue: QueuedOp[]): Record<number, string> {
  const problems: Record<number, string> = {};
  for (const { id, op } of queue) {
    if (op.op === 'set_text') {
      const limit = limitForPath(op.path);
      if (limit !== null && op.value.length > limit) {
        problems[id] = `Too long: ${op.value.length} of ${limit} characters.`;
      } else if (isRequiredPath(op.path) && !op.value.trim()) {
        problems[id] = 'This cannot be blank.';
      } else if (op.path === 'title.accent' && op.value && !titleText(draft.title).includes(op.value)) {
        problems[id] = 'The accent must appear in the title.';
      }
    } else if (op.op === 'rename_chapter') {
      if (!op.title.trim()) problems[id] = 'The chapter title cannot be blank.';
      else if (op.title.length > LIMITS['chapter.title']) problems[id] = 'The chapter title is too long.';
      else if ((op.kicker ?? '').length > LIMITS['chapter.kicker']) problems[id] = 'The kicker is too long.';
    }
  }
  return problems;
}

/** What has unsaved edits, for the dots in the outline. */
export function dirtyItems(queue: QueuedOp[]): { guide: boolean; chapters: Set<string>; picks: Set<string> } {
  const out = { guide: false, chapters: new Set<string>(), picks: new Set<string>() };
  for (const { op } of queue) {
    if (op.op === 'set_text') {
      const p = parsePath(op.path);
      if (p?.kind === 'pick') out.picks.add(p.id);
      else if (p?.kind === 'chapter') out.chapters.add(p.id);
      else out.guide = true;
    } else if (
      op.op === 'insert_paragraph' ||
      op.op === 'remove_paragraph' ||
      op.op === 'move_paragraph'
    ) {
      if (op.target === 'lede') out.guide = true;
      else out.picks.add(op.pickId as string);
    } else if (op.op === 'remove_meta' || op.op === 'move_pick') {
      out.picks.add(op.pickId);
    } else if (op.op === 'rename_chapter' || op.op === 'move_chapter') {
      out.chapters.add(op.chapterId);
    }
  }
  return out;
}

/** A short human description of an operation, for error lists and the history drawer. */
export function describeOp(op: { op?: unknown; [key: string]: unknown }): string {
  const s = (key: string) => (typeof op[key] === 'string' ? (op[key] as string) : '');
  switch (op.op) {
    case 'set_text': {
      const value = s('value');
      const shown = value.length > 60 ? `${value.slice(0, 57)}...` : value;
      return `Changed ${friendlyPath(s('path'))}${value ? ` to "${shown}"` : ' (cleared it)'}`;
    }
    case 'insert_paragraph':
      return `Added a paragraph to ${op.target === 'lede' ? 'the lede' : `"${s('pickId')}"`}`;
    case 'remove_paragraph':
      return `Removed a paragraph from ${op.target === 'lede' ? 'the lede' : `"${s('pickId')}"`}`;
    case 'move_paragraph':
      return `Moved paragraph ${Number(op.from) + 1} of ${
        op.target === 'lede' ? 'the lede' : `"${s('pickId')}"`
      } to position ${Number(op.to) + 1}`;
    case 'remove_meta':
      return `Removed meta line ${Number(op.index) + 1} from "${s('pickId')}"`;
    case 'remove_pick':
      return `Removed "${s('pickId')}" from the guide`;
    case 'move_pick':
      return `Moved "${s('pickId')}" to position ${Number(op.index) + 1} of "${s('chapterId')}"`;
    case 'add_pick_from_place':
      return `Added a place to "${s('chapterId')}"${s('title') ? `: ${s('title')}` : ''}`;
    case 'set_venue_link':
      return `Linked "${s('pickId')}" to a different place`;
    case 'add_chapter':
      return `Added the chapter "${s('title')}"`;
    case 'rename_chapter':
      return `Renamed chapter "${s('chapterId')}" to "${s('title')}"`;
    case 'move_chapter':
      return `Moved chapter "${s('chapterId')}" to position ${Number(op.index) + 1}`;
    case 'remove_chapter':
      return `Removed the chapter "${s('chapterId')}"`;
    case 'create_guide':
      return `Created the guide "${s('title')}" (${s('city')}, ${s('category')})`;
    case 'put_article':
      return 'Saved from Blog Studio';
    default:
      return typeof op.op === 'string' ? `Ran ${op.op}` : 'Unknown change';
  }
}

function friendlyPath(path: string): string {
  const p = parsePath(path);
  if (!p) return path;
  switch (p.kind) {
    case 'lede.paragraph':
      return `lede paragraph ${p.index + 1}`;
    case 'chapter':
      return `the ${p.field} of chapter "${p.id}"`;
    case 'pick': {
      if (p.field === 'paragraph') return `paragraph ${(p.index ?? 0) + 1} of "${p.id}"`;
      if (p.field === 'meta') return `meta line ${(p.index ?? 0) + 1} of "${p.id}"`;
      return `the ${p.field === 'marginalia' ? "editor's note" : p.field} of "${p.id}"`;
    }
    case 'title.text':
      return 'the guide title';
    case 'title.accent':
      return 'the title accent';
    case 'lede.headline':
      return 'the lede headline';
    case 'seo.title':
      return 'the SEO title';
    case 'seo.description':
      return 'the SEO description';
    default:
      return 'the dek';
  }
}
